import { MAX_X, MAX_Y } from "../FrenzyConstants";
import { FrenzyFilledPolygon, FrenzyPolygon, PixelField, Point } from "../Model";
import { isPointOnLine, translateX, translateY } from "../utils/Point";
import { contains, makeSparseArray, setBitAtPoint } from "../utils/SparseArray";
import { describePolygon, mostBottomRightInteriorPointForPolygon } from "../utils/Polygon";

export type FloodFillInProgressOutput = {
  partiallyFilledArea: PixelField
  complete: false
}

export type FloodFillCompleteOutput = {
  partiallyFilledArea: undefined
  complete: true
}

export type FloodFillOutput = FloodFillInProgressOutput | FloodFillCompleteOutput

export function floodFillNextTick(polygonToFill: FrenzyFilledPolygon, previousFilledArea: PixelField | undefined): FloodFillOutput {
  if (!previousFilledArea) {
    if (polygonToFill.innerArea === 0) {
      // console.log(`Special case: ${describePolygon(polygonToFill)} -> no fill needed`);
      return {
        partiallyFilledArea: undefined,
        complete: true
      }
    }

    const sparseArray = makeSparseArray(MAX_X, MAX_Y);
    const startPoint: Point = mostBottomRightInteriorPointForPolygon(polygonToFill);
    setBitAtPoint(sparseArray, startPoint);
    const partiallyFilledArea: PixelField = {
      sparseArray,
      newFrontier: new Set([startPoint]),
      color: polygonToFill.color,
    }
    // console.log(`Beginning fill of ${describePolygon(polygonToFill)} at ${describePoint(startPoint)}`)
    return {
      partiallyFilledArea,
      complete: false
    }
  } else {
    return expandFilledArea(polygonToFill, previousFilledArea as PixelField)
  }  
}

export function expandFilledArea(polygonToFill: FrenzyFilledPolygon, previousFilledArea: PixelField): FloodFillOutput {
  const { sparseArray, newFrontier: previousFrontier } = previousFilledArea;

  const newFrontier: Set<Point> = new Set();

  previousFrontier.forEach((p) => {
    // console.log(`Bit ${describePoint(p)} is set`)
    const expansions = expandPixel(polygonToFill, p);
    // console.log(`Expansions are ${JSON.stringify(expansions, null, 2)}`)

    const unseenExpansions = new Set(expansions.filter(x => !contains(sparseArray, x)));
    if (unseenExpansions.size > 0) {
      Array.from(unseenExpansions).forEach((usx) => {
        newFrontier.add(usx);
        setBitAtPoint(sparseArray, usx);
      });
    }
  });

  if (newFrontier.size === 0) {
    return { partiallyFilledArea: undefined, complete: true}
  } 

  // console.log(`New frontier: ${JSON.stringify(Array.from(newFrontier))}`)

  return {
    partiallyFilledArea: {
      sparseArray,
      newFrontier,
      color: polygonToFill.color,
    }, 
    complete: false
  }

}

export function expandPixel(polygonToFill: FrenzyPolygon, pixel: Point): Array<Point> {
  const nsew = [translateY(pixel, -1),
  translateY(pixel, 1),
  translateX(pixel, 1),
  translateX(pixel, -1)];
    const polyLines = polygonToFill.polygonLines;
  return nsew.filter((p) => !isPointOnLine(p, polyLines));
}

